/**
 * Motor de calificación NOM-035-STPS-2018.
 */

import { NOM35_G2_ITEMS, NOM35_G3_ITEMS } from '@/config/nom35Items';

/**
 * Regla NOM-035 Guía I: si en la Sección I (g1_1 a g1_6) todas son "No" (0),
 * finalizar la prueba al presionar Siguiente en la pregunta 6.
 */
export function nom35EarlyExitCheck(
  _currentAnswer: number,
  allAnswers: Record<string, number>,
  currentItemId: string,
  testId: string
): boolean {
  if (testId !== 'NOM35_G1') return false;
  if (currentItemId !== 'g1_6') return false;
  const section1Ids = ['g1_1', 'g1_2', 'g1_3', 'g1_4', 'g1_5', 'g1_6'];
  return section1Ids.every((id) => (allAnswers[id] ?? 0) === 0);
}

export type Nom35RiskLevel = 'Nulo' | 'Bajo' | 'Medio' | 'Alto' | 'Muy Alto';

export interface Nom35G1Result {
  requiresClinicalAttention: boolean;
  sectionIIAnyYes: boolean;
  sectionIIICount: number;
  sectionIVCount: number;
  earlyExit: boolean;
}

export interface Nom35G2G3Result {
  byCategory: Record<string, number>;
  byDomain: Record<string, number>;
  total: number;
  riskLevel: Nom35RiskLevel;
  maxPossible: number;
}

export function calculateSampleNom35(totalWorkers: number): number {
  if (totalWorkers <= 0) return 0;
  const N = totalWorkers;
  const n = (0.9604 * N) / (0.0025 * (N - 1) + 0.9604);
  return Math.ceil(n);
}

export type Nom35GuideType = 'NOM35_G1' | 'NOM35_G2' | 'NOM35_G3';

/**
 * Determina la guía adicional NOM-035 según el número de trabajadores.
 * Guía I (ATS) es OBLIGATORIA para todos los centros.
 * Guía II: Centros con 16 a 50 trabajadores.
 * Guía III: Centros con más de 50 trabajadores.
 * @returns La guía adicional a aplicar (II o III). Para <16 solo aplica Guía I.
 */
export function getNom35AdditionalGuideForCompanySize(activeEmployeeCount: number): Nom35GuideType | null {
  if (activeEmployeeCount >= 51) return 'NOM35_G3';
  if (activeEmployeeCount >= 16) return 'NOM35_G2';
  return null;
}

/**
 * Lista de guías a enviar según el tamaño del centro.
 * Guía I siempre obligatoria. Guía II o III adicional si 16+ empleados.
 */
export function getNom35GuidesToSend(activeEmployeeCount: number): Nom35GuideType[] {
  const additional = getNom35AdditionalGuideForCompanySize(activeEmployeeCount);
  if (additional) return ['NOM35_G1', additional];
  return ['NOM35_G1'];
}

const G1_SECTION_I = ['g1_1', 'g1_2', 'g1_3', 'g1_4', 'g1_5', 'g1_6'];
const G1_SECTION_II = ['g1_7', 'g1_8'];
const G1_SECTION_III = ['g1_9', 'g1_10', 'g1_11', 'g1_12', 'g1_13', 'g1_14', 'g1_15'];
const G1_SECTION_IV = ['g1_16', 'g1_17', 'g1_18', 'g1_19', 'g1_20'];

function getG1Value(answers: Record<string, number>, id: string): number {
  const v = answers[id];
  return v === 1 ? 1 : 0;
}

export function scoreNom35G1(answers: Record<string, number>): Nom35G1Result {
  const sectionIValues = G1_SECTION_I.map((id) => getG1Value(answers, id));
  const allSectionINo = sectionIValues.every((a) => a === 0);
  const earlyExit = allSectionINo;

  const sectionIIAnyYes = G1_SECTION_II.some((id) => getG1Value(answers, id) === 1);
  const sectionIIICount = G1_SECTION_III.filter((id) => getG1Value(answers, id) === 1).length;
  const sectionIVCount = G1_SECTION_IV.filter((id) => getG1Value(answers, id) === 1).length;

  const requiresClinicalAttention =
    !earlyExit &&
    (sectionIIAnyYes || sectionIIICount >= 3 || sectionIVCount >= 2);

  return {
    requiresClinicalAttention,
    sectionIIAnyYes,
    sectionIIICount,
    sectionIVCount,
    earlyExit,
  };
}

const RISK_THRESHOLDS_G2: { max: number; level: Nom35RiskLevel }[] = [
  { max: 46, level: 'Nulo' },
  { max: 83, level: 'Bajo' },
  { max: 120, level: 'Medio' },
  { max: 156, level: 'Alto' },
  { max: Infinity, level: 'Muy Alto' },
];

const RISK_THRESHOLDS_G3: { max: number; level: Nom35RiskLevel }[] = [
  { max: 43, level: 'Nulo' },
  { max: 72, level: 'Bajo' },
  { max: 101, level: 'Medio' },
  { max: 115, level: 'Alto' },
  { max: Infinity, level: 'Muy Alto' },
];

function getRiskLevel(score: number, thresholds: { max: number; level: Nom35RiskLevel }[]): Nom35RiskLevel {
  for (const t of thresholds) {
    if (score <= t.max) return t.level;
  }
  return 'Muy Alto';
}

export function scoreNom35Likert(
  answers: number[],
  items: { reverse?: boolean; category?: string; domain?: string }[],
  thresholds: { max: number; level: Nom35RiskLevel }[]
): Nom35G2G3Result {
  const byCategory: Record<string, number> = {};
  const byDomain: Record<string, number> = {};
  let total = 0;

  for (let i = 0; i < items.length; i++) {
    const raw = answers[i] ?? 0;
    const value = items[i].reverse ? raw : 4 - raw;
    total += value;

    const cat = items[i].category ?? '';
    const dom = items[i].domain ?? '';
    if (cat) byCategory[cat] = (byCategory[cat] ?? 0) + value;
    if (dom) byDomain[dom] = (byDomain[dom] ?? 0) + value;
  }

  const maxPossible = items.length * 4;
  const riskLevel = getRiskLevel(total, thresholds);

  return {
    byCategory,
    byDomain,
    total,
    riskLevel,
    maxPossible,
  };
}

export function scoreNom35G2(
  answers: number[],
  items: { reverse?: boolean; category?: string; domain?: string }[]
): Nom35G2G3Result {
  return scoreNom35Likert(answers, items, RISK_THRESHOLDS_G2);
}

export function scoreNom35G3(
  answers: number[],
  items: { reverse?: boolean; category?: string; domain?: string }[]
): Nom35G2G3Result {
  return scoreNom35Likert(answers, items, RISK_THRESHOLDS_G3);
}

const G2_CATEGORIES: { start: number; end: number; name: string }[] = [
  { start: 1, end: 9, name: 'Ambiente de trabajo' },
  { start: 10, end: 13, name: 'Responsabilidades' },
  { start: 14, end: 17, name: 'Tiempo y familia' },
  { start: 18, end: 22, name: 'Control y decisiones' },
  { start: 23, end: 27, name: 'Capacitación' },
  { start: 28, end: 33, name: 'Liderazgo y relaciones' },
  { start: 34, end: 40, name: 'Violencia laboral' },
  { start: 41, end: 43, name: 'Atención a usuarios' },
  { start: 44, end: 46, name: 'Jefatura' },
];

const G3_CATEGORIES: { start: number; end: number; name: string }[] = [
  { start: 1, end: 5, name: 'Entorno de trabajo' },
  { start: 6, end: 12, name: 'Carga de trabajo' },
  { start: 13, end: 16, name: 'Responsabilidades' },
  { start: 17, end: 22, name: 'Jornada laboral' },
  { start: 23, end: 28, name: 'Control y decisiones' },
  { start: 29, end: 30, name: 'Cambios organizacionales' },
  { start: 31, end: 36, name: 'Capacitación' },
  { start: 37, end: 41, name: 'Liderazgo' },
  { start: 42, end: 46, name: 'Relaciones laborales' },
  { start: 47, end: 57, name: 'Reconocimiento y pertenencia' },
  { start: 58, end: 64, name: 'Violencia laboral' },
  { start: 65, end: 72, name: 'Atención y jefatura' },
];

function getCategoryForItem(guideType: 'NOM35_G2' | 'NOM35_G3', itemNumber: number): string {
  const cats = guideType === 'NOM35_G2' ? G2_CATEGORIES : G3_CATEGORIES;
  const found = cats.find((c) => itemNumber >= c.start && itemNumber <= c.end);
  return found?.name ?? 'Otros';
}

function getCategoryRiskLevel(score: number, maxPossible: number): Nom35RiskLevel {
  if (maxPossible <= 0) return 'Nulo';
  const pct = score / maxPossible;
  if (pct <= 0.25) return 'Nulo';
  if (pct <= 0.45) return 'Bajo';
  if (pct <= 0.65) return 'Medio';
  if (pct <= 0.85) return 'Alto';
  return 'Muy Alto';
}

export interface CategoryScoreRow {
  name: string;
  score: number;
  maxPossible: number;
  level: Nom35RiskLevel;
}

export function computeCategoryScoresFromAnswers(
  answers: Record<string, number>,
  guideType: 'NOM35_G2' | 'NOM35_G3'
): { result: Nom35G2G3Result; categoryRows: CategoryScoreRow[] } {
  const items = guideType === 'NOM35_G2' ? NOM35_G2_ITEMS : NOM35_G3_ITEMS;

  const itemsWithCategory = items.map((item) => ({
    reverse: item.reverse,
    category: getCategoryForItem(guideType, item.number),
  }));

  const answersArray = items.map((item) => answers[item.id] ?? 0);
  const thresholds = guideType === 'NOM35_G2' ? RISK_THRESHOLDS_G2 : RISK_THRESHOLDS_G3;
  const result = scoreNom35Likert(answersArray, itemsWithCategory, thresholds);

  const categoryRows: CategoryScoreRow[] = Object.entries(result.byCategory).map(([name, score]) => {
    const itemCount = itemsWithCategory.filter((i) => i.category === name).length;
    const maxPossible = itemCount * 4;
    return {
      name,
      score,
      maxPossible,
      level: getCategoryRiskLevel(score, maxPossible),
    };
  });

  return { result, categoryRows };
}
